import React, { useCallback, useEffect, useRef, useState } from "react";
import { Alert, Button, Card, Spinner } from "react-bootstrap";
import { BsCalendar3, BsCheckCircle, BsClock, BsClockHistory, BsHeartPulse, BsInfoCircle, BsXCircle } from "react-icons/bs";
import { fetchRiwayatJurnal } from "../services/riwayatService";
import BottomNavbar from "../components/BottomNavbar";
import logo from "../assets/EPRAJOGO1.png";

function dateLabel(value) {
  if (!value) return "Tanggal tidak tersedia";
  const datePart = String(value).split("T")[0].split(" ")[0];
  const [year, month, day] = datePart.split("-").map(Number);
  if (!year || !month || !day) return datePart;
  return new Date(year, month - 1, day).toLocaleDateString("id-ID", {
    weekday: "long", day: "numeric", month: "long", year: "numeric",
  });
}

function permitStatus(status) {
  if (status === "menunggu") return { label: "Menunggu verifikasi", color: "warning", icon: <BsClock /> };
  if (status === "izin") return { label: "Disetujui guru", color: "success", icon: <BsCheckCircle /> };
  return { label: "Ditolak guru", color: "danger", icon: <BsXCircle /> };
}

function RiwayatPresensi() {
  const [riwayat, setRiwayat] = useState([]);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState("");
  const siswaId = localStorage.getItem("user_id");
  const fetchedPages = useRef(new Set());
  const loadingRef = useRef(false);
  const loadMoreRef = useRef(null);

  const loadData = useCallback(async (currentPage) => {
    if (!siswaId || loadingRef.current || fetchedPages.current.has(currentPage)) return;
    loadingRef.current = true;
    setLoading(true);
    setLoadError("");
    try {
      const result = await fetchRiwayatJurnal(siswaId, currentPage);
      if (!result.success) {
        setLoadError(result.message || "Riwayat gagal dimuat.");
        return;
      }

      const entries = result.data?.data || [];
      setRiwayat((previous) => [...previous, ...entries]);
      fetchedPages.current.add(currentPage);
      setPage(currentPage);
      setHasMore((result.data?.current_page || 1) < (result.data?.last_page || 1));
    } catch (error) {
      setLoadError(error.message || "Riwayat gagal dimuat.");
    } finally {
      loadingRef.current = false;
      setLoading(false);
    }
  }, [siswaId]);

  useEffect(() => {
    loadData(1);
  }, [loadData]);

  useEffect(() => {
    if (!hasMore || loading || loadError || riwayat.length === 0) return undefined;
    const sentinel = loadMoreRef.current;
    if (!sentinel) return undefined;

    if ("IntersectionObserver" in window) {
      const observer = new IntersectionObserver((entries) => {
        if (entries.some((entry) => entry.isIntersecting)) loadData(page + 1);
      }, { rootMargin: "160px" });
      observer.observe(sentinel);
      return () => observer.disconnect();
    }

    const onScroll = () => {
      if (sentinel.getBoundingClientRect().top <= window.innerHeight + 160) loadData(page + 1);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [hasMore, loading, loadError, riwayat.length, page, loadData]);

  const retry = () => loadData(page + 1);

  const renderEntry = (item, index) => {
    const isPermit = ["menunggu", "izin", "ditolak"].includes(item.status);
    const status = permitStatus(item.status);
    const type = item.jenis_izin === "sakit" ? "Sakit" : "Izin";
    const date = dateLabel(item.tanggal_izin || item.created_at);

    return (
      <Card key={item.id || `${item.created_at}-${index}`} className="history-card mb-3">
        <Card.Body className="p-3">
          <div className="d-flex align-items-start justify-content-between gap-3 pb-3 mb-3 border-bottom">
            <div className="d-flex align-items-center gap-3">
              <div className={`history-icon ${isPermit ? "permit" : "attendance"}`}>
                {isPermit ? <BsHeartPulse /> : <BsCalendar3 />}
              </div>
              <div>
                <div className="fw-bold">{date}</div>
                <small className="text-muted">{isPermit ? `Pengajuan ${type.toLowerCase()}` : "Catatan presensi"}</small>
              </div>
            </div>
            {isPermit ? (
              <span className={`history-badge ${status.color}`}><span aria-hidden="true">{status.icon}</span>{status.label}</span>
            ) : (
              <span className={`history-badge ${item.keterangan_check_in === "Terlambat" || item.status === "terlambat" ? "warning" : "success"}`}>
                {item.keterangan_check_in || (item.status === "terlambat" ? "Terlambat" : "Hadir")}
              </span>
            )}
          </div>

          {isPermit ? (
            <>
              <div className="small text-muted fw-semibold mb-1">Alasan pengajuan</div>
              <p className="mb-0" style={{ whiteSpace: "pre-wrap" }}>{item.keterangan || "Tidak ada keterangan."}</p>
              {item.status === "menunggu" && <div className="history-notice mt-3"><BsInfoCircle /> Pengajuanmu sedang menunggu keputusan Guru Pembimbing.</div>}
              {item.status === "ditolak" && <div className="history-notice rejected mt-3"><BsInfoCircle /> Pengajuan ditolak. Hubungi Guru Pembimbing bila perlu mengajukan ulang.</div>}
            </>
          ) : (
            <div className="row g-2">
              <div className="col-6"><div className="history-time"><small><BsClock className="me-1" />Masuk</small><strong>{item.jam_check_in || "—"}</strong></div></div>
              <div className="col-6"><div className="history-time"><small><BsClock className="me-1" />Keluar</small><strong>{item.jam_check_out || "Belum tercatat"}</strong></div></div>
            </div>
          )}
        </Card.Body>
      </Card>
    );
  };

  return (
    <main className="app-mobile-page">
      <header className="app-brand-header">
        <div className="app-brand-row">
          <img src={logo} alt="Logo E-Prajogo" className="app-brand-logo" />
          <div><p className="app-brand-name">E-PRAJOGO</p><p className="app-brand-school">SMK NEGERI GUDO</p></div>
          <span className="ms-auto rounded-pill px-3 py-2 small fw-semibold" style={{ background: "#ffffff20", color: "#fff" }}>RIWAYAT</span>
        </div>
        <h1 className="app-header-title">Riwayat Presensi</h1>
        <p className="app-header-subtitle">Pantau catatan kehadiran dan status pengajuan izin PKL.</p>
      </header>

      <section className="app-content">
        {loadError && <Alert variant="danger" className="rounded-3 d-flex align-items-center justify-content-between gap-2"><span>{loadError}</span><Button variant="outline-danger" size="sm" onClick={retry}>Coba lagi</Button></Alert>}
        {riwayat.map(renderEntry)}
        {loading && <div className="text-center py-4"><Spinner animation="border" variant="primary" /><div className="small text-muted mt-2">Memuat riwayat...</div></div>}
        {!loading && !loadError && riwayat.length === 0 && <Card className="history-card"><Card.Body className="text-center py-5"><div className="history-icon attendance mx-auto mb-3"><BsClockHistory /></div><h2 className="h6 fw-bold">Belum ada catatan</h2><p className="small text-muted mb-0">Presensi dan pengajuan izinmu akan muncul di sini.</p></Card.Body></Card>}
        {!loading && hasMore && riwayat.length > 0 && (
          <div ref={loadMoreRef} className="text-center py-2" aria-live="polite">
            {!("IntersectionObserver" in window) && <Button variant="outline-primary" className="w-100 rounded-3" onClick={() => loadData(page + 1)}>Tampilkan lebih banyak</Button>}
            {"IntersectionObserver" in window && <span className="small text-muted">Gulir untuk memuat riwayat berikutnya</span>}
          </div>
        )}
        {!loading && !hasMore && riwayat.length > 0 && <p className="text-center small text-muted mt-3">Kamu sudah melihat semua riwayat.</p>}
      </section>
      <BottomNavbar />
    </main>
  );
}

export default RiwayatPresensi;
